/**
 * خدمات قوالب المهام المتكررة.
 *
 * القالب منفصل عن نسخ التنفيذ: تعديل القالب لا يغيّر النسخ المولّدة سابقًا،
 * ويطبق فقط على المواعيد التي لم تُولّد بعد. التوليد نفسه يعتمد على قاعدة
 * idempotency (`occurrenceKey`) وعلى `generatedThrough`.
 */

import type { DataGateway, GenerateOccurrencesResult } from '@/data/types';
import type {
  AssignmentMode,
  CompletionRule,
  RecurrenceRule,
  TaskPriority,
  TaskTemplate,
  TaskType,
  TaskVisibility,
} from '@/domain/types';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import { addDaysLocal, normalizeRule, todayInTimeZone, validateRule } from '@/domain/recurrence';
import { AUDIT_ACTIONS } from '@/domain/audit';
import { SETTING_KEYS, type TemplateChecklistStepDef } from '@/domain/settings';
import type { Actor } from './org-service';

export interface TemplateInput {
  title: string;
  description: string | null;
  instructions: string | null;
  taskType: TaskType;
  priority: TaskPriority;
  branchId: string | null;
  brandId: string | null;
  categoryId: string;
  recurrence: RecurrenceRule;
  startDate: string;
  startTime: string;
  durationMinutes: number;
  timeZone: TaskTemplate['timeZone'];
  assignmentMode: AssignmentMode;
  completionRule: CompletionRule;
  assigneeMembershipIds: string[];
  requiresProof: boolean;
  requiresApproval: boolean;
  approverMembershipId: string | null;
  isPersonal: boolean;
  visibility: TaskVisibility;
}

export async function listTemplates(
  gateway: DataGateway,
  companyId: string,
  includeArchived = false,
): Promise<TaskTemplate[]> {
  const rows = await gateway.table('task_templates').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
      ...(includeArchived ? {} : { status: { op: 'eq', value: 'active' as const } }),
    },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });
  return rows.filter((row) => !row.deletedAt);
}

export async function getTemplate(gateway: DataGateway, templateId: string): Promise<TaskTemplate> {
  const template = await gateway.table('task_templates').get(templateId);
  if (!template) throw AppError.notFound('القالب غير موجود');
  return template;
}

function validateInput(input: TemplateInput): RecurrenceRule {
  const title = input.title.trim();
  if (title.length < 2 || title.length > 200) {
    throw AppError.validation('عنوان القالب يجب أن يكون بين حرفين و200 حرف');
  }
  const rule = normalizeRule(input.recurrence);
  const issues = validateRule(rule, input.startDate);
  if (issues.length > 0) throw AppError.validation(issues[0]!.message);
  if (!/^\d{2}:\d{2}$/.test(input.startTime)) {
    throw AppError.validation('وقت البدء غير صالح');
  }
  if (input.durationMinutes < 0 || input.durationMinutes > 24 * 60) {
    throw AppError.validation('المدة يجب أن تكون بين 0 و1440 دقيقة');
  }
  if (input.assigneeMembershipIds.length === 0 && input.assignmentMode !== 'scope') {
    throw AppError.validation('اختر مسؤولًا واحدًا على الأقل');
  }
  return rule;
}

export async function createTemplate(
  gateway: DataGateway,
  actor: Actor,
  input: TemplateInput,
): Promise<TaskTemplate> {
  const rule = validateInput(input);
  if (actor.membershipId) {
    const creator = await gateway.table('memberships').get(actor.membershipId);
    if (!creator || creator.companyId !== actor.companyId) throw AppError.forbidden('العضوية غير صالحة');
  } else if (!actor.isPlatformSupport) {
    throw AppError.forbidden('إنشاء القالب يتطلب عضوية منشأة أو جلسة دعم منصة نشطة');
  }

  const now = new Date().toISOString();
  const template: TaskTemplate = {
    id: newId(),
    companyId: actor.companyId,
    brandId: input.brandId,
    branchId: input.branchId,
    categoryId: input.categoryId,
    title: input.title.trim(),
    description: input.description?.trim() || null,
    instructions: input.instructions?.trim() || null,
    taskType: input.taskType,
    priority: input.priority,
    recurrence: rule,
    startDate: input.startDate,
    startTime: input.startTime,
    durationMinutes: input.durationMinutes,
    timeZone: input.timeZone,
    assignmentMode: input.assignmentMode,
    completionRule: input.completionRule,
    assigneeScope: {
      branchIds: input.branchId ? [input.branchId] : [],
      brandIds: input.brandId ? [input.brandId] : [],
      membershipIds: input.assigneeMembershipIds,
      includeSupervised: input.assignmentMode === 'scope',
    },
    requiresProof: input.requiresProof,
    requiresApproval: input.requiresApproval,
    approverMembershipId: input.approverMembershipId,
    isPersonal: input.isPersonal,
    visibility: input.visibility,
    status: 'active',
    generatedThrough: null,
    createdByMembershipId: actor.membershipId,
    createdByUserId: actor.userId,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  };

  const created = await gateway.table('task_templates').insert(template);
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskTemplateCreated,
    entityType: 'task_template',
    entityId: created.id,
    after: { title: created.title, frequency: rule.frequency, startDate: input.startDate },
  });
  return created;
}

export async function updateTemplate(
  gateway: DataGateway,
  actor: Actor,
  templateId: string,
  input: TemplateInput,
): Promise<TaskTemplate> {
  const rule = validateInput(input);
  const existing = await getTemplate(gateway, templateId);
  if (existing.companyId !== actor.companyId) throw AppError.forbidden('القالب تابع لمنشأة أخرى');

  const updated = await gateway.table('task_templates').update(templateId, {
    title: input.title.trim(),
    description: input.description?.trim() || null,
    instructions: input.instructions?.trim() || null,
    taskType: input.taskType,
    priority: input.priority,
    branchId: input.branchId,
    brandId: input.brandId,
    categoryId: input.categoryId,
    recurrence: rule,
    startDate: input.startDate,
    startTime: input.startTime,
    durationMinutes: input.durationMinutes,
    timeZone: input.timeZone,
    assignmentMode: input.assignmentMode,
    completionRule: input.completionRule,
    assigneeScope: {
      branchIds: input.branchId ? [input.branchId] : [],
      brandIds: input.brandId ? [input.brandId] : [],
      membershipIds: input.assigneeMembershipIds,
      includeSupervised: input.assignmentMode === 'scope',
    },
    requiresProof: input.requiresProof,
    requiresApproval: input.requiresApproval,
    approverMembershipId: input.approverMembershipId,
    isPersonal: input.isPersonal,
    visibility: input.visibility,
    updatedAt: new Date().toISOString(),
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskTemplateUpdated,
    entityType: 'task_template',
    entityId: templateId,
    before: {
      title: existing.title,
      recurrence: existing.recurrence,
      startDate: existing.startDate,
    },
    after: { title: updated.title, recurrence: updated.recurrence, startDate: updated.startDate },
  });
  return updated;
}

export async function archiveTemplate(
  gateway: DataGateway,
  actor: Actor,
  templateId: string,
): Promise<void> {
  const template = await getTemplate(gateway, templateId);
  if (template.companyId !== actor.companyId) throw AppError.forbidden('القالب تابع لمنشأة أخرى');
  await gateway.table('task_templates').update(templateId, {
    status: 'archived',
    updatedAt: new Date().toISOString(),
  });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskTemplateArchived,
    entityType: 'task_template',
    entityId: templateId,
  });
}

export async function generateOccurrences(
  gateway: DataGateway,
  templateId: string,
  throughDate: string,
  actorUserId: string | null,
): Promise<GenerateOccurrencesResult> {
  const template = await gateway.table('task_templates').get(templateId);
  if (!template) throw AppError.notFound('القالب غير موجود');

  const today = todayInTimeZone(template.timeZone);
  const yesterday = addDaysLocal(today, -1);
  // التوليد اليوم بيومه فقط: لا يتجاوز اليوم الحالي، ولا يولّد أيامًا ماضية فائتة
  const effectiveThrough = throughDate > today ? today : throughDate;

  if (!template.generatedThrough || template.generatedThrough < yesterday) {
    try {
      await gateway.table('task_templates').update(templateId, {
        generatedThrough: yesterday,
        updatedAt: new Date().toISOString(),
      });
    } catch {
      // نتجاهل إذا لم تكن صلاحية التعديل المباشر متاحة للعضو؛ الدالة الخادمية تتولى الباقي.
    }
  }

  const result = await gateway.commands.generateOccurrences({
    templateId,
    throughDate: effectiveThrough,
    actorUserId,
  });

  if (result.created > 0) {
    try {
      const settingsRows = await gateway.table('settings').list({
        filter: {
          scopeType: { op: 'eq', value: 'company' },
          scopeId: { op: 'eq', value: template.companyId },
          key: { op: 'eq', value: SETTING_KEYS.templateChecklists },
        },
      });
      const checklistsMap =
        (settingsRows[0]?.value as Record<string, TemplateChecklistStepDef[]> | undefined) ?? {};
      const stepsDef = checklistsMap[templateId] ?? [];
      if (stepsDef.length > 0) {
        const todayTasks = await gateway.table('tasks').list({
          filter: {
            companyId: { op: 'eq', value: template.companyId },
            templateId: { op: 'eq', value: templateId },
            localDate: { op: 'eq', value: effectiveThrough },
            deletedAt: { op: 'isNull' },
          },
        });
        for (const t of todayTasks) {
          const existingSteps = await gateway.table('task_steps').list({
            filter: { taskId: { op: 'eq', value: t.id } },
          });
          if (existingSteps.length === 0) {
            for (let i = 0; i < stepsDef.length; i += 1) {
              const step = stepsDef[i]!;
              await gateway.table('task_steps').insert({
                id: newId(),
                taskId: t.id,
                title: step.title,
                sortOrder: i + 1,
                isDone: false,
                doneByMembershipId: null,
                doneAt: null,
              });
            }
          }
        }
      }
    } catch {
      // في حال لم يملك المستخدم الحالي صلاحية كتابة الخطوات، نتجاوز بصمت
    }
  }

  return result;
}

export async function saveTemplateChecklistSteps(
  gateway: DataGateway,
  companyId: string,
  userId: string,
  templateId: string,
  steps: TemplateChecklistStepDef[],
): Promise<void> {
  const existingRows = await gateway.table('settings').list({
    filter: {
      scopeType: { op: 'eq', value: 'company' },
      scopeId: { op: 'eq', value: companyId },
      key: { op: 'eq', value: SETTING_KEYS.templateChecklists },
    },
  });
  const currentRow = existingRows[0] ?? null;
  const currentMap =
    (currentRow?.value as Record<string, TemplateChecklistStepDef[]> | undefined) ?? {};
  const nextMap: Record<string, TemplateChecklistStepDef[]> = {
    ...currentMap,
    [templateId]: steps.filter((s) => s.title.trim().length > 0),
  };
  const now = new Date().toISOString();
  if (currentRow) {
    await gateway.table('settings').update(currentRow.id, {
      value: nextMap,
      updatedBy: userId,
      updatedAt: now,
    });
  } else {
    await gateway.table('settings').insert({
      id: newId(),
      scopeType: 'company',
      scopeId: companyId,
      key: SETTING_KEYS.templateChecklists,
      value: nextMap,
      updatedBy: userId,
      updatedAt: now,
    });
  }
}

const lastEnsuredTodayMap = new Map<string, number>();

/**
 * ضمان توليد جولات اليوم الحالي تلقائيًا لجميع القوالب النشطة في المنشأة (اليوم بيومه فقط).
 */
export async function ensureTodayRecurringOccurrences(
  gateway: DataGateway,
  companyId: string,
  actorUserId: string | null,
): Promise<number> {
  const cacheKey = `${companyId}:${new Date().toISOString().slice(0, 10)}`;
  const lastTime = lastEnsuredTodayMap.get(cacheKey) ?? 0;
  // تفادي التكرار إذا تم الفحص خلال آخر 60 ثانية
  if (Date.now() - lastTime < 60_000) {
    return 0;
  }
  lastEnsuredTodayMap.set(cacheKey, Date.now());
  try {
    const activeTemplates = await listTemplates(gateway, companyId, false);
    let totalCreated = 0;
    for (const template of activeTemplates) {
      if (template.recurrence.frequency === 'once') continue;
      const today = todayInTimeZone(template.timeZone);
      if (template.startDate > today) continue;
      if (template.generatedThrough && template.generatedThrough >= today) continue;
      try {
        const res = await generateOccurrences(gateway, template.id, today, actorUserId);
        totalCreated += res.created;
      } catch {
        // لا نوقف بقية القوالب إذا تعذر توليد قالب واحد
      }
    }
    return totalCreated;
  } catch {
    return 0;
  }
}

export async function cancelFutureOccurrences(
  gateway: DataGateway,
  actor: Actor,
  templateId: string,
  fromDate: string,
  reason: string,
): Promise<number> {
  if (!reason.trim()) throw AppError.validation('سبب الإلغاء مطلوب');
  return gateway.commands.cancelTemplateOccurrences({
    templateId,
    fromDate,
    actorUserId: actor.userId,
    actorMembershipId: actor.membershipId,
    reason: reason.trim(),
  });
}

export type TemplateTasksDeleteMode = 'uncompleted' | 'all' | 'none';

export async function deleteTemplate(
  gateway: DataGateway,
  actor: Actor,
  templateId: string,
  tasksDeleteMode: TemplateTasksDeleteMode = 'uncompleted',
): Promise<{ deletedTasksCount: number }> {
  const template = await getTemplate(gateway, templateId);
  if (template.companyId !== actor.companyId) {
    throw AppError.forbidden('القالب تابع لمنشأة أخرى');
  }

  const templateTasks = await gateway.table('tasks').list({
    filter: {
      companyId: { op: 'eq', value: actor.companyId },
      templateId: { op: 'eq', value: templateId },
      deletedAt: { op: 'isNull' },
    },
  });

  const tasksToDelete =
    tasksDeleteMode === 'all'
      ? templateTasks
      : tasksDeleteMode === 'uncompleted'
        ? templateTasks.filter((t) => t.status !== 'completed')
        : [];

  let deletedTasksCount = 0;
  for (const task of tasksToDelete) {
    await gateway.commands.deleteTask({
      taskId: task.id,
      actorUserId: actor.userId,
    });
    deletedTasksCount += 1;
  }

  const now = new Date().toISOString();
  const allTasksRemoved = deletedTasksCount === templateTasks.length;

  if (allTasksRemoved) {
    try {
      await gateway.table('task_templates').remove(templateId);
    } catch {
      await gateway.table('task_templates').update(templateId, {
        status: 'archived',
        deletedAt: now,
        updatedAt: now,
      });
    }
  } else {
    await gateway.table('task_templates').update(templateId, {
      status: 'archived',
      deletedAt: now,
      updatedAt: now,
    });
  }

  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskTemplateDeleted,
    entityType: 'task_template',
    entityId: templateId,
    before: { title: template.title, branchId: template.branchId },
    after: { tasksDeleteMode, deletedTasksCount },
  });

  return { deletedTasksCount };
}

export function adaptTitleForBranch(input: {
  baseTitle: string;
  sourceBranchId: string | null;
  targetBranchId: string | null;
  branchNames: Map<string, string>;
  autoAdjustBranchName: boolean;
}): string {
  const trimmed = input.baseTitle.trim();
  if (!input.autoAdjustBranchName) return trimmed;

  const sourceBranchName = input.sourceBranchId
    ? input.branchNames.get(input.sourceBranchId)?.trim()
    : null;
  const targetBranchName = input.targetBranchId
    ? input.branchNames.get(input.targetBranchId)?.trim()
    : null;

  if (sourceBranchName && targetBranchName && trimmed.includes(sourceBranchName)) {
    return trimmed.replace(sourceBranchName, targetBranchName).trim();
  }
  if (targetBranchName && input.targetBranchId !== input.sourceBranchId && !trimmed.includes(targetBranchName)) {
    return `${trimmed} - ${targetBranchName}`;
  }
  if (input.targetBranchId === input.sourceBranchId) {
    return `${trimmed} (نسخة)`;
  }
  return trimmed;
}

export interface CloneTemplateToBranchesInput {
  templateId: string;
  targetBranchIds: Array<string | null>;
  customBaseTitle?: string;
  autoAdjustBranchName: boolean;
  assigneeMode: 'branch_scope' | 'same_assignees' | 'custom';
  customAssigneeIds: string[];
  startDate?: string;
  generateThroughDate?: string | null;
  branchNames: Map<string, string>;
  branchBrandMap: Map<string, string | null>;
}

export async function cloneTemplateToBranches(
  gateway: DataGateway,
  actor: Actor,
  input: CloneTemplateToBranchesInput,
): Promise<{ createdTemplates: TaskTemplate[]; generatedCount: number }> {
  const source = await getTemplate(gateway, input.templateId);
  if (source.companyId !== actor.companyId) {
    throw AppError.forbidden('القالب تابع لمنشأة أخرى');
  }
  if (input.targetBranchIds.length === 0) {
    throw AppError.validation('اختر فرعًا واحدًا على الأقل للاستنساخ');
  }

  const baseTitle = input.customBaseTitle?.trim() || source.title;
  const createdTemplates: TaskTemplate[] = [];
  let generatedCount = 0;

  for (const targetBranchId of input.targetBranchIds) {
    const nextTitle = adaptTitleForBranch({
      baseTitle,
      sourceBranchId: source.branchId,
      targetBranchId,
      branchNames: input.branchNames,
      autoAdjustBranchName: input.autoAdjustBranchName,
    });

    const targetBrandId = targetBranchId
      ? (input.branchBrandMap.get(targetBranchId) ?? source.brandId)
      : source.brandId;

    const assignmentMode: AssignmentMode =
      input.assigneeMode === 'branch_scope'
        ? 'scope'
        : input.assigneeMode === 'custom'
          ? input.customAssigneeIds.length > 1
            ? 'multi'
            : 'single'
          : source.assignmentMode;

    const assigneeMembershipIds =
      input.assigneeMode === 'branch_scope'
        ? []
        : input.assigneeMode === 'custom'
          ? input.customAssigneeIds
          : source.assigneeScope.membershipIds;

    const cloned = await createTemplate(gateway, actor, {
      title: nextTitle,
      description: source.description,
      instructions: source.instructions,
      taskType: source.taskType,
      priority: source.priority,
      branchId: targetBranchId,
      brandId: targetBrandId,
      categoryId: source.categoryId,
      recurrence: source.recurrence,
      startDate: input.startDate || source.startDate,
      startTime: source.startTime,
      durationMinutes: source.durationMinutes,
      timeZone: source.timeZone,
      assignmentMode,
      completionRule: source.completionRule,
      assigneeMembershipIds,
      requiresProof: source.requiresProof,
      requiresApproval: source.requiresApproval,
      approverMembershipId: source.approverMembershipId,
      isPersonal: source.isPersonal,
      visibility: source.visibility,
    });

    createdTemplates.push(cloned);

    if (input.generateThroughDate) {
      const genResult = await generateOccurrences(
        gateway,
        cloned.id,
        input.generateThroughDate,
        actor.userId,
      );
      generatedCount += genResult.created;
    }
  }

  await gateway.recordAudit({
    companyId: actor.companyId,
    action: AUDIT_ACTIONS.taskTemplateCloned,
    entityType: 'task_template',
    entityId: source.id,
    after: {
      sourceTitle: source.title,
      targetBranchIds: input.targetBranchIds,
      clonedTemplateIds: createdTemplates.map((t) => t.id),
      generatedCount,
    },
  });

  return { createdTemplates, generatedCount };
}
